import { z } from "zod";
import { toNumOrUndef } from "./toNumOrUndef";

/** Helper for 0–100% radius fields */
const percentField = (invalidMsg, minMsg, maxMsg) =>
  z.coerce.number(invalidMsg).min(0, minMsg).max(100, maxMsg);

const CompCollisionOpt = z.enum(["na", "1000", "2500"]); // N/A, $1,000, $2,500
const CurrentBILimitOpt = z.enum([
  "25k-50k",
  "50k-100k",
  "100k_csl",
  "100k-300k",
  "300k_csl",
  "250k-500k",
  "500k_csl",
  "750k_csl",
  "1m_csl",
]);

/** Annual mileage (must be ≥1, but can be empty while typing) */
const annualMilesField = z.preprocess(
  toNumOrUndef,
  z
    .number("Please enter your estimated annual mileage")
    .min(1, "Annual mileage must be at least 1 mile")
    .max(999_999, "Annual mileage cannot exceed 999,999 miles")
);

const NonOwnedTrailerType = z.enum(["dry_freight", "flatbed", "refer", "gooseneck"]);
const NonOwnedTrailerCoverageAmt = z.enum(["15000", "20000", "30000", "40000", "50000", "60000", "70000", "80000"]);
const NonOwnedTrailerDeductible = z.enum(["1000", "2000"]); 

const isNum = (n) => typeof n === "number" && Number.isFinite(n);

const emptyToUndefNumber = (v) => {
  if (v === "" || v == null) return undefined;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? Number(n) : undefined;
};

export const Step8Schema = z
  .object({
    carrier: z.array(z.enum(["Cover Whale", "Progressive", "Nirvana", "Accelerant"])).default([]),
    /* ---------- Coverages (flat names to match previous impl) ---------- */
    auto_liability: z.boolean().default(false),
    auto_liability_limit_for_cover_whale_and_nirvana: z.string().optional().nullable(),
    auto_liability_limit_for_progressive: z.string().optional().nullable(),

    personal_injury_protection: z.boolean().default(false),
    uninsured_motorists_property_damage: z.boolean().default(false),

    trailer_interchange: z.boolean().default(false),
    trailer_interchange_limit: z.string().optional().nullable(),

    motor_truck_cargo: z.boolean().default(false),
    motor_truck_cargo_limit: z.string().optional().nullable(),
    mtc_deductible: z.string().optional().nullable(),

    automobile_physical_damage: z.boolean().default(false),
    truckers_general_liability: z.boolean().default(false),
    non_trucking_liability: z.boolean().default(false),

    /* ---------- Limits ---------- */
    limits: z.object({
      waiverSubrogation: z.boolean().default(false),
      primaryNoncontribTGL: z.boolean().default(false),
      primaryNoncontribAL: z.boolean().default(false),
      towingLimit: z.string().optional().nullable(), // validated below
    }),

    /* ---------- Cargo ---------- */
    commodity_array: z
      .array(
        z.object({
          commodityKey: z.string().min(1, "Please select a commodity"),
          commodityPercentage: z
            .number({ invalid_type_error: "Commodity Percentage is required" })
            .min(1, "Percentage need to be at least 1%")
            .max(100, "Percentage need to be maximum 100%"),
          refrigerated: z.boolean().default(false),
          generalMerchDetail: z.string().optional(),
        })
      )
      .default([])
      .superRefine((items, ctx) => {
        if (!items.length) return;
        const total = items.reduce((acc, curr) => acc + curr.commodityPercentage, 0);

        if (total > 100) {
          ctx.addIssue({
            code: 'custom',
            message: `Total percentage can not be greater than 100. Currently: ${total}%`,
          });
        }

        if (total < 100) {
          ctx.addIssue({
            code: 'custom',
            message: `Total percentage must be exactly 100%. Currently: ${total}%`,
          });
        }

        items.forEach((item, idx) => {
          if (
            item.commodityKey === "general_merchandise" &&
            (!item.generalMerchDetail || item.generalMerchDetail.trim() === "")
          ) {
            ctx.addIssue({
              code: "custom",
              path: [idx, "generalMerchDetail"],
              message: "Please provide details for General Merchandise",
            });
          }
        });
      }),
    stateMileage: z
      .array(
        z.object({
          state: z.string().min(1, "Please select a state mileage"),
          percentage: z.coerce
            .number({ invalid_type_error: "Mileage Percentage is required" })
            .min(1, "Percentage need to be at least 1%")
            .max(100, "Percentage need to be maximum 100%"),
          miles: z.preprocess(
            toNumOrUndef,
            z
              .number({ invalid_type_error: "Miles must be a number" })
              .min(0, "Miles cannot be negative")
          ),
        })
      )
      .default([])
      .superRefine((items, ctx) => {
        if (!items.length) {
          return;
        }

        const totalPct = items.reduce((acc, curr) => acc + (curr?.percentage ?? 0), 0);
        if (totalPct > 100) {
          ctx.addIssue({
            code: 'custom',
            message: `Total percentage can not be greater than 100. Currently: ${totalPct}%`,
          });
        }
        if (totalPct < 100) {
          ctx.addIssue({
            code: 'custom',
            message: `Total percentage must be exactly 100%. Currently: ${totalPct}%`,
          });
        }
      }),


    /* ---------- Radius ---------- */
    radius0_50: percentField(
      "Please enter the percentage of trips under 50 miles",
      "Percentage of trips under 50 miles cannot be negative",
      "Percentage of trips under 50 miles cannot exceed 100%"
    ),
    radius51_200: percentField(
      "Please enter the percentage of trips between 51-200 miles",
      "Percentage of trips between 51-200 miles cannot be negative",
      "Percentage of trips between 51-200 miles cannot exceed 100%"
    ),
    radius201_500: percentField(
      "Please enter the percentage of trips between 201-500 miles",
      "Percentage of trips between 201-500 miles cannot be negative",
      "Percentage of trips between 201-500 miles cannot exceed 100%"
    ),
    radius501: percentField(
      "Please enter the percentage of trips over 500 miles",
      "Percentage of trips over 500 miles cannot be negative",
      "Percentage of trips over 500 miles cannot exceed 100%"
    ),

    // If you also support "all", include it here; otherwise keep just the two.
    stateOperation: z.enum(
      ["interstate", "intrastate", "all"],
      "Please select your state operation type from the list"
    ),

    annualMileage: annualMilesField,

    currentlyInsured: z.enum(["yes", "no", "na"])
      .optional(),
    continuousCoverage: z.enum(["yes", "no", "na"])
      .optional(),

    currentBILimitChecked: z.boolean().default(false),
    currentBILimitValue: CurrentBILimitOpt.optional(),

    hiredAutoLiability: z.boolean().default(false),
    hiredAutoCount: z.preprocess(
      toNumOrUndef,
      z.number({ invalid_type_error: "Please enter a number" }).min(0, "Cannot be negative").optional()
    ),

    rentalReimbursement: z.literal("yes").optional(),
    roadsideAssistance: z.literal("yes").optional(),

    blanketAdditionalInsured: z.literal("yes").optional(),

    compCollisionDeductibleChecked: z.boolean().default(false),
    compCollisionDeductibleOption: CompCollisionOpt.optional(),

    nonOwnedTrailerCoverage: z.literal("yes").optional(), // checked -> send "yes"
    nonOwnedTrailersCount: z.preprocess(
      emptyToUndefNumber,
      z.union([
        z
          .number({ invalid_type_error: "Only numbers 1-9 will be accepted" })
          .int("Only numbers 1-9 will be accepted")
          .min(1, "Only numbers 1-9 will be accepted")
          .max(9, "Only numbers 1-9 will be accepted"),
        z.undefined(), // <- empty is OK
      ]),
    ),
    nonOwnedTrailers: z
      .array(
        z.object({
          trailerType: NonOwnedTrailerType.optional(),
          coverageAmount: NonOwnedTrailerCoverageAmt.optional(),
          deductible: NonOwnedTrailerDeductible.optional(),
        })
      )
      .optional(),
  })
  /* ---------- Conditional requirements (mirror Yup logic) ---------- */
  .superRefine((v, ctx) => {
    const notUnselected = (s) => !!s && s !== "unselected";
    const carriers = (v.carrier ?? []).map((c) => (c ?? ""));
    const hasCoverWhale = carriers.includes("Cover Whale");
    const hasProg = carriers.includes("Progressive");
    const hasNir = carriers.includes("Nirvana");
    const hasAccelerant = carriers.includes("Accelerant");

    if (v.auto_liability && (hasCoverWhale || hasNir || hasAccelerant) && !notUnselected(v.auto_liability_limit_for_cover_whale_and_nirvana)) {
      ctx.addIssue({
        code: "custom",
        path: ["auto_liability_limit_for_cover_whale_and_nirvana"],
        message: "Please select Auto Liability Limit For Cover Whale, Accelerant, And Nirvana",
      });
    }
    if (v.auto_liability && hasProg && !notUnselected(v.auto_liability_limit_for_progressive)) {
      ctx.addIssue({
        code: "custom",
        path: ["auto_liability_limit_for_progressive"],
        message: "Please select Auto Liability Limit For Progressive",
      });
    }
    if (v.trailer_interchange && !notUnselected(v.trailer_interchange_limit)) {
      ctx.addIssue({
        code: "custom",
        path: ["trailer_interchange_limit"],
        message: "Please select a Trailer Interchange Limit",
      });
    }
    if (v.motor_truck_cargo && !notUnselected(v.motor_truck_cargo_limit)) {
      ctx.addIssue({
        code: "custom",
        path: ["motor_truck_cargo_limit"],
        message: "Please select a Motor Truck Cargo Limit",
      });
    }
    if (v.motor_truck_cargo && (hasProg || hasNir || hasAccelerant) && !notUnselected(v.mtc_deductible)) {
      ctx.addIssue({
        code: "custom",
        path: ["mtc_deductible"],
        message: "Please select MTC deductible",
      });
    }

    if ((v.motor_truck_cargo || hasCoverWhale) && (v.commodity_array ?? []).length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["commodity_array"],
        message: "Please add at least one commodity",
      });
    }

    if (!notUnselected(v.limits?.towingLimit)) {
      ctx.addIssue({
        code: "custom",
        path: ["limits", "towingLimit"],
        message: "Please select a Towing, Labor and Storage Limit",
      });
    }

    if (!v.truckers_general_liability && v.limits?.primaryNoncontribTGL) {
      ctx.addIssue({
        code: "custom",
        path: ["limits", "primaryNoncontribTGL"],
        message:
          "Primary & Noncontributory for TGL applies only when TGL is selected",
      });
    }

    const stateMileage = v.stateMileage ?? [];
    if (hasAccelerant && stateMileage.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["stateMileage"],
        message: "Please add at least one state mileage entry (required for Accelerant)",
      });
    }

    const annual = v.annualMileage;
    if (stateMileage.length > 0 && typeof annual === "number" && Number.isFinite(annual) && annual > 0) {
      let missingMiles = false;
      let totalMiles = 0;
      stateMileage.forEach((sm, idx) => {
        if (sm.miles == null || !Number.isFinite(sm.miles)) {
          missingMiles = true;
          ctx.addIssue({
            code: "custom",
            path: ["stateMileage", idx, "miles"],
            message: "Please enter miles for this state",
          });
        } else {
          totalMiles += sm.miles;
        }
      });
      if (!missingMiles && totalMiles !== annual) {
        ctx.addIssue({
          code: "custom",
          path: ["stateMileage"],
          message: `State miles must total annual mileage (${annual}). Currently: ${totalMiles}`,
        });
      }
    }

    if (hasProg && (v.currentlyInsured == null || v.currentlyInsured === "na")) {
      ctx.addIssue({ code: "custom", path: ["currentlyInsured"], message: "Please answer whether the customer is currently insured" });
    }
    if (hasProg && (v.continuousCoverage == null || v.continuousCoverage === "na")) {
      ctx.addIssue({ code: "custom", path: ["continuousCoverage"], message: "Please answer whether the customer had continuous coverage" });
    }

    if (hasProg && v.currentBILimitChecked && !v.currentBILimitValue) {
      ctx.addIssue({ code: "custom", path: ["currentBILimitValue"], message: "Please select a Current Bodily Injury Liability Limit" });
    }

    if (hasProg && v.hiredAutoLiability && v.hiredAutoCount == null) {
      ctx.addIssue({
        code: "custom",
        path: ["hiredAutoCount"],
        message: "Please enter how many autos were rented/hired/borrowed in the last year",
      });
    }

    if ((hasProg || hasNir) && v.compCollisionDeductibleChecked && !v.compCollisionDeductibleOption) {
      ctx.addIssue({ code: "custom", path: ["compCollisionDeductibleOption"], message: "Please select a Comp/Collision Deductible" });
    }

    if (v.nonOwnedTrailerCoverage === "yes") {
      // validate only when count is a real number
      if (typeof v.nonOwnedTrailersCount === "number" && Number.isFinite(v.nonOwnedTrailersCount)) {
        const countNum = v.nonOwnedTrailersCount;
        const list = v.nonOwnedTrailers ?? [];

        if (list.length !== countNum) {
          ctx.addIssue({
            code: "custom",
            path: ["nonOwnedTrailers"],
            message: `Please enter details for ${countNum} trailer(s).`,
          });
        }

        list.forEach((t, i) => {
          if (!t.trailerType) {
            ctx.addIssue({
              code: "custom",
              path: ["nonOwnedTrailers", i, "trailerType"],
              message: "Please select Type of Trailer",
            });
          }
          if (!t.coverageAmount) {
            ctx.addIssue({
              code: "custom",
              path: ["nonOwnedTrailers", i, "coverageAmount"],
              message: "Please select Amount of Non-Owned Trailer Coverage",
            });
          }
          if (hasProg && !t.deductible) {
            ctx.addIssue({
              code: "custom",
              path: ["nonOwnedTrailers", i, "deductible"],
              message: "Please select Non-Owned Trailer Deductible",
            });
          }
        });
      }
      // else: count empty → no errors (let user type)
    }

    const r0 = v.radius0_50;
    const r1 = v.radius51_200;
    const r2 = v.radius201_500;
    const r3 = v.radius501;

    // radius51_200 max = 100 - r0
    if (isNum(r1) && isNum(r0)) {
      const max1 = Math.max(0, 100 - r0);
      if (r1 > max1) {
        ctx.addIssue({
          code: "custom",
          path: ["radius51_200"],
          message: `Cannot exceed ${max1}% given Under 50 miles = ${r0}%.`,
        });
      }
    }

    // radius201_500 max = 100 - r0 - r1 (when r0/r1 are numbers)
    if (isNum(r2)) {
      let max2 = 100;
      if (isNum(r0)) max2 -= r0;
      if (isNum(r1)) max2 -= r1;
      max2 = Math.max(0, max2);
      if (r2 > max2) {
        ctx.addIssue({
          code: "custom",
          path: ["radius201_500"],
          message: `Cannot exceed ${max2}% given prior entries.`,
        });
      }
    }

    // radius501 max = 100 - r0 - r1 - r2 (when prior are numbers)
    if (isNum(r3)) {
      let max3 = 100;
      if (isNum(r0)) max3 -= r0;
      if (isNum(r1)) max3 -= r1;
      if (isNum(r2)) max3 -= r2;
      max3 = Math.max(0, max3);
      if (r3 > max3) {
        ctx.addIssue({
          code: "custom",
          path: ["radius501"],
          message: `Cannot exceed ${max3}% given prior entries.`,
        });
      }
    }

    // total must be exactly 100 once all four are numbers
    if ([v.radius0_50, v.radius51_200, v.radius201_500, v.radius501].every(isNum)) {
      const total = v.radius0_50 + v.radius51_200 + v.radius201_500 + v.radius501;
      if (total !== 100) {
        ["radius0_50", "radius51_200", "radius201_500", "radius501"].forEach((field) => {
          ctx.addIssue({
            code: "custom",
            path: [field],
            message: `Radius percentages must total 100%. Currently: ${total}`,
          });
        });
      }
    }
  });
